import {
  DailyCardsAssignment,
  DailyCardsExperimentConfig,
  DailyCardsVariantOverrides,
  DeckAllowance,
} from "./models/DailyCardsExperiment";

/** Timestamps above this are milliseconds; at or below, seconds. */
const MAX_SECONDS_TIMESTAMP = 9_999_999_999;

interface AllowanceConstants {
  numberOfCardsPerBatch?: number;
  secondsToReleaseNewCard?: number;
}

export function normalizeExperimentTimestampToMs(
  value: unknown,
): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value > MAX_SECONDS_TIMESTAMP
      ? Math.floor(value)
      : Math.floor(value * 1000);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseOverrides(raw: unknown): DailyCardsVariantOverrides {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const record = raw as Record<string, unknown>;
  const overrides: DailyCardsVariantOverrides = {};
  if (
    typeof record.numberOfCardsPerBatch === "number" &&
    Number.isInteger(record.numberOfCardsPerBatch) &&
    record.numberOfCardsPerBatch > 0
  ) {
    overrides.numberOfCardsPerBatch = record.numberOfCardsPerBatch;
  }
  if (
    typeof record.secondsToReleaseNewCard === "number" &&
    Number.isFinite(record.secondsToReleaseNewCard) &&
    record.secondsToReleaseNewCard > 0
  ) {
    overrides.secondsToReleaseNewCard = record.secondsToReleaseNewCard;
  }
  return overrides;
}

/**
 * Fail-open parse of `abTesting/definitions/dailyCards`: anything malformed
 * yields `null`, which the resolver treats as "experiment off".
 */
export function parseDailyCardsExperimentConfig(
  raw: unknown,
): DailyCardsExperimentConfig | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const variants: Record<string, DailyCardsVariantOverrides> = {};
  if (record.variants && typeof record.variants === "object") {
    Object.entries(record.variants as Record<string, unknown>).forEach(
      ([name, overrides]) => {
        if (name.trim()) {
          variants[name] = parseOverrides(overrides);
        }
      },
    );
  }
  return {
    enabled: record.enabled === true,
    startedAt: normalizeExperimentTimestampToMs(record.startedAt),
    variants,
  };
}

export function parseDailyCardsAssignment(
  raw: unknown,
): DailyCardsAssignment | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (typeof record.variant !== "string" || !record.variant.trim()) {
    return null;
  }
  const assignment: DailyCardsAssignment = {
    variant: record.variant,
    assignedAt: typeof record.assignedAt === "number" ? record.assignedAt : 0,
    source: "signup",
    cohortStartedAt:
      typeof record.cohortStartedAt === "number"
        ? record.cohortStartedAt
        : null,
  };
  if (typeof record.inheritedFrom === "string") {
    assignment.inheritedFrom = record.inheritedFrom;
  }
  if (typeof record.previousVariant === "string") {
    assignment.previousVariant = record.previousVariant;
  }
  if (typeof record.reassignedAt === "number") {
    assignment.reassignedAt = record.reassignedAt;
  }
  if (
    record.excludedReason === "partnerUnassigned" ||
    record.excludedReason === "coupledAfterPlay"
  ) {
    assignment.excludedReason = record.excludedReason;
    if (typeof record.excludedAt === "number") {
      assignment.excludedAt = record.excludedAt;
    }
  }
  return assignment;
}

/** The overrides in effect, or null when the experiment does not apply to this user. */
function activeOverrides(
  definition: DailyCardsExperimentConfig | null | undefined,
  assignment: DailyCardsAssignment | null | undefined,
): DailyCardsVariantOverrides | null {
  if (!definition || !definition.enabled || !assignment) {
    return null;
  }
  return definition.variants[assignment.variant] ?? {};
}

/**
 * The deck allowance for this user. Base constants unless the experiment is
 * switched on AND the user carries an assignment; then the variant's
 * overrides win field by field. A variant with no overrides (the control
 * arm) still reports its name so exposure and analytics are tagged.
 */
export function resolveDeckAllowance(
  constants: AllowanceConstants | null | undefined,
  definition: DailyCardsExperimentConfig | null | undefined,
  assignment: DailyCardsAssignment | null | undefined,
): DeckAllowance {
  const overrides = activeOverrides(definition, assignment);
  if (!overrides || !assignment) {
    return {
      numberOfCardsPerBatch: constants?.numberOfCardsPerBatch,
      secondsToReleaseNewCard: constants?.secondsToReleaseNewCard,
      variant: null,
    };
  }
  return {
    numberOfCardsPerBatch:
      overrides.numberOfCardsPerBatch ?? constants?.numberOfCardsPerBatch,
    secondsToReleaseNewCard:
      overrides.secondsToReleaseNewCard ?? constants?.secondsToReleaseNewCard,
    variant: assignment.variant,
  };
}

/**
 * Changes exactly when the deck would be dealt differently: lets a live deck
 * session reload on a kill-switch flip or a late assignment, and ignore
 * writes that do not affect this user.
 */
export function dailyCardsStateKey(
  definition: DailyCardsExperimentConfig | null | undefined,
  assignment: DailyCardsAssignment | null | undefined,
): string {
  const overrides = activeOverrides(definition, assignment);
  if (!overrides || !assignment) {
    return "off";
  }
  return [
    assignment.variant,
    overrides.numberOfCardsPerBatch ?? "",
    overrides.secondsToReleaseNewCard ?? "",
  ].join("|");
}
